import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Mail, Shield, CheckCircle, Clock, Search, X } from 'lucide-react';
import api from '../services/api';

const TeamMembersTab = ({ role }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [inviteError, setInviteError] = useState('');
    const [inviteSuccess, setInviteSuccess] = useState('');
    const [isInviting, setIsInviting] = useState(false);

    // Determine base API path based on role
    const apiPath = role === 'sourcing_partner' ? '/sourcing/team' : '/recruiting/team';

    useEffect(() => {
        fetchMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get(apiPath);
            setMembers(response.data || []);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch team members');
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        setInviteError('');
        setInviteSuccess('');
        setIsInviting(true);

        try {
            const response = await api.post(`${apiPath}/invite`, {
                name: inviteName,
                email: inviteEmail
            });
            setInviteSuccess(response.data.message || 'Team member invited successfully!');
            setInviteName('');
            setInviteEmail('');
            fetchMembers();

            setTimeout(() => {
                setIsInviteModalOpen(false);
                setInviteSuccess('');
            }, 2000);
        } catch (err) {
            setInviteError(err.response?.data?.error || 'Failed to invite team member');
        } finally {
            setIsInviting(false);
        }
    };

    if (loading && members.length === 0) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                <div>
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Users className="h-6 w-6 text-cyan-500" />
                        Team Members
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">Manage users who can access your company dashboard</p>
                </div>
                <button
                    onClick={() => setIsInviteModalOpen(true)}
                    className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-md flex items-center gap-2"
                >
                    <UserPlus className="h-4 w-4" />
                    Invite Member
                </button>
            </div>

            {error ? (
                <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">
                    {error}
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                                    <th className="p-4 font-semibold">User</th>
                                    <th className="p-4 font-semibold">Role</th>
                                    <th className="p-4 font-semibold">Status</th>
                                    <th className="p-4 font-semibold">Joined</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {members.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-8 text-center text-slate-500">
                                            You are the only member of this company. Invite others to give them access.
                                        </td>
                                    </tr>
                                ) : (
                                    members.map(member => (
                                        <tr key={member.id} className="hover:bg-slate-50 transition-colors">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 font-bold shrink-0">
                                                        {member.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-slate-800">{member.name}</div>
                                                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                            <Mail className="h-3 w-3" />
                                                            {member.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-medium capitalize">
                                                    <Shield className="h-3.5 w-3.5" />
                                                    {member.role?.replace('_', ' ')}
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                {member.status === 'approved' ? (
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-xs font-medium">
                                                        <CheckCircle className="h-3.5 w-3.5" />
                                                        Active
                                                    </div>
                                                ) : (
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-yellow-50 text-yellow-700 text-xs font-medium">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        Pending Approval
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-4 text-sm text-slate-600">
                                                {new Date(member.created_at).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Invite Modal */}
            {isInviteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh] animate-in slide-in-from-bottom-4">
                        <div className="flex justify-between items-center p-6 border-b border-slate-100">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800">Invite Team Member</h3>
                                <p className="text-xs text-slate-500 mt-1">Pending approval from Staff</p>
                            </div>
                            <button
                                onClick={() => setIsInviteModalOpen(false)}
                                className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 rounded-full"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <form onSubmit={handleInvite} className="p-6 overflow-y-auto">
                            {inviteError && (
                                <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-xl border border-red-100">
                                    {inviteError}
                                </div>
                            )}
                            {inviteSuccess && (
                                <div className="mb-4 p-3 bg-green-50 text-green-700 text-sm rounded-xl border border-green-100 font-medium">
                                    {inviteSuccess}
                                </div>
                            )}

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={inviteName}
                                        onChange={(e) => setInviteName(e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all outline-none"
                                        placeholder="Enter member's name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Email Address</label>
                                    <input
                                        type="email"
                                        required
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all outline-none"
                                        placeholder="member@company.com"
                                    />
                                    <p className="text-[10px] text-slate-500 mt-1">
                                        An initial placeholder password will be set automatically.
                                    </p>
                                </div>
                            </div>

                            <div className="mt-8 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsInviteModalOpen(false)}
                                    className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isInviting}
                                    className="flex-1 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isInviting ? (
                                        <div className="animate-spin h-5 w-5 border-2 border-white/30 border-t-white rounded-full" />
                                    ) : (
                                        <>
                                            <UserPlus className="h-4 w-4" />
                                            Send Invite
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamMembersTab;
